import { Box, HStack, Tooltip } from '@saas-ui/react'

import { ProgressIcon } from '#components/ui/progress-icon.tsx'
import {
  getStatusLabel,
  isProgressingActivity,
  statusConfig,
} from '#features/implementations/utils/step-labels'
import type { ImplementationFromZero } from '#zero/implementation-from-zero.ts'

type Implementation = ImplementationFromZero

export function getProgressForStatus(
  status: Implementation['status'],
  progress?: number,
): number | undefined {
  return status === 'completed' || status === 'merged' ? 1 : progress
}

/**
 * Fraction of steps that are completed, as a `0-1` value for `ProgressIcon`.
 *
 * Zero-safe: returns `0` for empty or missing step lists so the icon never
 * receives `NaN`. Shared by the sidebar, task list, home lists, and step
 * hovercard so all four status icons depict the same partial-fill fraction.
 */
export function getStepProgress<TStep extends { status: string | null }>(
  steps: ReadonlyArray<TStep> | null | undefined,
): number {
  if (!steps?.length) return 0

  const completedSteps = steps.filter(
    (step) => step.status === 'completed',
  ).length
  return completedSteps / steps.length
}

export type StatusDisplayInput = {
  status: Implementation['status']
  activity?: Implementation['activity']
  waitingForInput?: boolean
  currentStepName?: string | null
}

export type StatusDisplay = {
  label: string
  detail: string | null
  colorPalette: string
  loading: boolean
}

/**
 * Resolves the label, colour and spinner state for an implementation status.
 *
 * Kept as a pure function (and the only place the label is derived) because
 * list views no longer render the label as text — it lives in a tooltip. A
 * second copy of this resolution at a call site would silently drift from what
 * the icon actually depicts.
 */
export function resolveStatusDisplay({
  status,
  activity,
  waitingForInput,
  currentStepName,
}: StatusDisplayInput): StatusDisplay {
  const activityKey = activity?.key
  const config = statusConfig[
    activityKey ?? (waitingForInput ? 'waiting_for_input' : status)
  ] ?? {
    colorPalette: 'gray',
    state: 'default',
    label: activity?.label ?? 'Unknown status',
  }

  let label = activity?.label ?? config.label

  if (
    !activity &&
    status === 'running' &&
    currentStepName &&
    !waitingForInput
  ) {
    label = getStatusLabel(currentStepName) ?? config.label
  }

  return {
    label,
    detail:
      activity?.key === 'needs_attention' ? (activity.detail ?? null) : null,
    colorPalette: config.colorPalette,
    loading:
      status === 'running' &&
      (!activity || isProgressingActivity(activity.key)),
  }
}

export function ImplementationStatus({
  status,
  activity,
  hideLabel = false,
  waitingForInput,
  currentStepName,
  progress,
}: {
  status: Implementation['status']
  activity?: Implementation['activity']
  hideLabel?: boolean
  waitingForInput?: boolean
  currentStepName?: string | null
  progress?: number
}) {
  const { label, detail, colorPalette, loading } = resolveStatusDisplay({
    status,
    activity,
    waitingForInput,
    currentStepName,
  })

  const icon = (
    <ProgressIcon
      loading={loading}
      value={getProgressForStatus(status, progress)}
      colorPalette={colorPalette}
    />
  )

  // Without a visible label the icon alone carries the status, so surface it on
  // hover. `lazyMount`/`unmountOnExit` keep the tooltip content out of the tree
  // until hovered — list views render one of these per row.
  //
  // The trigger renders as a plain `div` (asChild), and `aria-label` on an
  // element with the implicit `generic` role is dropped by screen readers —
  // `role="img"` is what makes the label the icon's accessible name, since it
  // is no longer on screen as text.
  if (hideLabel) {
    return (
      <Tooltip
        content={detail ? `${label}: ${detail}` : label}
        positioning={{ placement: 'top' }}
        openDelay={300}
        closeDelay={50}
        portalled
        lazyMount
        unmountOnExit
      >
        <Box
          display="flex"
          alignItems="center"
          flexShrink={0}
          role="img"
          aria-label={detail ? `${label}: ${detail}` : label}
        >
          {icon}
        </Box>
      </Tooltip>
    )
  }

  return (
    <HStack gap="2" textStyle="sm" alignItems="center">
      {icon}
      <Box>{label}</Box>
    </HStack>
  )
}
